import { ErrorResponse } from "../api/monolith";
import {
  LOGIN_PAGE_PATHNAME,
  MAIN_PAGE_PATHNAME,
  QUERY_PARAM_CLIENT,
  LOGIN_REQUEST_PATHNAME,
  OTP_CHECK_REQUEST_PATHNAME,
  LOGIN_BY_PASSWORD_REQUEST_PATHNAME,
  LOGIN_BY_CHECK_SMS_CODE_PATHNAME,
  LOGIN_BY_CERTIFICATE_PATHNAME,
  MONOLITH_LOGOUT_REQUEST_PATHNAME,
  MS_LOGOUT_REQUEST_PATHNAME,
} from "./constants";

export const checkIsLoginPage = () =>
  window.location.pathname === LOGIN_PAGE_PATHNAME;

export const checkIsLoginZonePage = () =>
  window.location.pathname.startsWith(LOGIN_PAGE_PATHNAME);

export const checkIsMainPage = () =>
  window.location.pathname === MAIN_PAGE_PATHNAME;

export const isLoginRequest = (path: string) => path === LOGIN_REQUEST_PATHNAME;

export const isMsLoginRequest = (path: string) =>
  path === LOGIN_BY_PASSWORD_REQUEST_PATHNAME;

export const isLoginOrOtpCheckRequest = (path: string) =>
  isLoginRequest(path) || path === OTP_CHECK_REQUEST_PATHNAME;

export const isLogoutRequest = (path: string) =>
  path === MONOLITH_LOGOUT_REQUEST_PATHNAME ||
  path === MS_LOGOUT_REQUEST_PATHNAME;

export const isLoginOrCheckSMSCodeOrCheckCertificatetRequest = (path: string) =>
  path === LOGIN_BY_PASSWORD_REQUEST_PATHNAME ||
  path === LOGIN_BY_CHECK_SMS_CODE_PATHNAME ||
  path === LOGIN_BY_CERTIFICATE_PATHNAME;

export const getQueryParamValue = (param: string) =>
  new URLSearchParams(window.location.search).get(param);

/**
 * Производим проверку что у пользователя в query-параметрах есть
 * соответствующие атрибуты для предоставления ему функциональности SSO.
 *
 * Данный сценарий предполагает аутентификацию пользователя проекта Свое Фермерство
 * через форму логина проекта Свой Бизнес. После логина в СБ происходит редирект в проект СФ.
 *
 * Пример адреса - https://dev-ikul.rshb.ru/login?client=farming&path=/main/?auth=authsb
 */
export const isLoginPageWithLoginSso = () =>
  checkIsLoginPage() &&
  (getQueryParamValue(QUERY_PARAM_CLIENT)?.length ?? 0) > 0;

/**
 * INFO: На основании наличия подстроки в пароле, определяем что данный пользователь
 * производит вход с использованием сертификата
 */
export const isUserWithCertificate = (password: string) =>
  password.startsWith("success_cryptopro_web_plugin");

/**
 * INFO: Утилита форматирующая логин и пароль пользователя для сценариев SSO и МС Аутентификации.
 *
 * Данная функциональность является временной и должна быть мигрирована в проект Логина!
 *
 * В случае аутентификации с использованием сертификата, пароль пользователя будет иметь вид
 * `success_cryptopro_web_plugin$$${cryptoProSignature}$$${login}$$${password}`.
 *
 * Данный вид пароля формируется на уровне проекта Логина, в котором метод prepareAuthParametrs
 * формирует логин пользователя на основании ID сертификата, а пароль из электронной подписи CryptoPro +
 * данных введённых в форме входа (логин, пароль). После чего вызывается рест монолита /login и тд.
 *
 * МС Аутентификации данный формат не поддерживает и ожидает логин и пароль без указанных выше преобразований.
 */
export const getUserCredentials = (login: string, password: string) => {
  if (isUserWithCertificate(password)) {
    // Пример: "success_cryptopro_web_plugin$$${cryptoProSignature}$$laskin$$12345" => ["success_cryptopro_web_plugin", "${cryptoProSignature}", "laskin", "12345"]
    const splittedPassword = password.split("$$");

    return {
      login: splittedPassword[2],
      password: splittedPassword[3],
    };
  }

  // Если пользователь производит вход без использования сертификата, то возвращаем логин и пароль без дополнительных преобразований
  return { login, password };
};

export const isMonolithErrorResponse = (
  response: unknown
): response is ErrorResponse =>
  !!response &&
  typeof response === "object" &&
  "errors" in response &&
  Array.isArray((response as ErrorResponse).errors);
